import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ClipboardPaste } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTran, useFormDirty, useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";
import {
  useTargetSetupStore,
  useTargetSetupUIStore,
} from "../hooks/useTargetSetupStore";
import { bulkSetValueToAll, loadOrgList } from "../operations/target-setup.ops";
import {
  buildEmptyTargetData,
  buildEmptyMonthlyTargetData,
  buildDisclosureYearKey,
  hasInvalidNegativeZeroTargetValue,
  isMonthlyMode,
  resolveDisclosureId,
} from "../domains/targetSetup";
import {
  buildAssignedOrgTree,
  collectAssignedTargetScopeIds,
} from "../domains/assignmentScope";
import { countOrgs } from "../domains/orgTree.utils";
import TargetValueFields from "./TargetValueFields";

const hasFilledValue = (value) =>
  value !== null && value !== undefined && value !== "";

const SetValueToAllDialog = observer(({ getReportingPeriodMonths }) => {
  const { t } = useTran();
  const {
    filteredOrgTree,
    orgList,
    orgTreeLoaded,
    targetFrequency,
    getOrgTargets,
  } = useTargetSetupStore();
  const { user } = useAuthStore();
  const {
    showSetValueToAllDialog,
    setValueToAllDisclosure: disclosure,
    closeSetValueToAllDialog,
    bulkSetting,
    loadingOrgTree,
    getDisclosureTargetFrequency,
  } = useTargetSetupUIStore();

  const [values, setValues] = useState({});
  const isDirty = useFormDirty(values, showSetValueToAllDialog);
  const { showDialog, confirmDiscard, cancelDiscard, guardAction } =
    useUnsavedChanges(isDirty, { blockNavigation: false });

  const year = disclosure?.masterDataYear || disclosure?.masterDataYears?.[0];
  const monthOptions = useMemo(
    () => getReportingPeriodMonths?.(year),
    [getReportingPeriodMonths, year],
  );
  const disclosureId = resolveDisclosureId(disclosure);
  const disclosureKey = buildDisclosureYearKey(
    disclosureId || disclosure?.code || disclosure?.title,
    year,
  );
  const showPeriodToggle = user?.enableMonthlyYearlySwitch === true;
  const selectedDisclosureTargetFrequency = getDisclosureTargetFrequency(
    disclosureKey,
    targetFrequency,
  );
  const disclosureTargetFrequency = showPeriodToggle
    ? selectedDisclosureTargetFrequency
    : targetFrequency;
  const isMonthly = isMonthlyMode(disclosureTargetFrequency);
  const inputType = disclosure?.inputType || "";
  const unitOptions = disclosure?.unitOptions || [];

  useEffect(() => {
    if (showSetValueToAllDialog && !orgTreeLoaded) {
      void loadOrgList();
    }
  }, [showSetValueToAllDialog, orgTreeLoaded]);

  const assignedOrgTree = useMemo(
    () => buildAssignedOrgTree(disclosure, filteredOrgTree),
    [disclosure, filteredOrgTree],
  );
  const organizationIds = useMemo(
    () =>
      disclosure?.isAllOrganization
        ? "all"
        : collectAssignedTargetScopeIds(assignedOrgTree),
    [assignedOrgTree, disclosure?.isAllOrganization],
  );
  const orgCount = useMemo(
    () =>
      disclosure?.isAllOrganization
        ? orgList.length
        : countOrgs(assignedOrgTree),
    [assignedOrgTree, disclosure?.isAllOrganization, orgList.length],
  );
  const canApply =
    orgTreeLoaded && (organizationIds === "all" || organizationIds.length > 0);

  const handleOpenChange = (open) => {
    if (!open) {
      guardAction(() => closeSetValueToAllDialog());
      return;
    }
  };

  const handleApply = async () => {
    const success = await bulkSetValueToAll({
      disclosureId,
      year,
      targetData: values,
      organizationIds,
      inputType,
      disclosure,
      targetFrequency: disclosureTargetFrequency,
    });
    if (success) {
      setValues({});
    }
  };

  const handleEnter = () => {
    if (isMonthly && orgCount === 1 && disclosureId && year) {
      const orgTargetsMap = getOrgTargets(disclosureId, year);
      const firstEntry = orgTargetsMap.values().next().value;
      if (firstEntry?.targetData) {
        const existingMonths = buildEmptyMonthlyTargetData();
        const storedMonths =
          firstEntry.targetData.months ||
          (firstEntry.targetData.monthlyValues
            ? Object.fromEntries(
                firstEntry.targetData.monthlyValues.map((mv) => {
                  const idx = monthOptions?.findIndex(
                    (opt) => opt.month === mv.month && opt.year === mv.year,
                  );
                  const key = idx >= 0 ? String(idx + 1) : String(mv.month);
                  return [key, mv.value ?? ""];
                }),
              )
            : {});
        setValues({ months: { ...existingMonths.months, ...storedMonths } });
        return;
      }
    }
    setValues(
      isMonthly
        ? buildEmptyMonthlyTargetData()
        : buildEmptyTargetData(disclosure),
    );
  };

  const hasValue = isMonthly
    ? Object.values(values.months ?? {}).some(hasFilledValue)
    : hasFilledValue(values.value);
  const hasInvalidNegativeZero = hasInvalidNegativeZeroTargetValue(
    values,
    inputType,
  );

  return (
    <>
      <Dialog open={showSetValueToAllDialog} onOpenChange={handleOpenChange}>
        <DialogContent onOpenAutoFocus={handleEnter}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5 text-primary" />
              {t("Set Value to All Organizations")}
            </DialogTitle>
          </DialogHeader>

          {disclosure ? (
            <>
              {/* Disclosure info */}
              <div className="rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-mono">
                    {disclosure.code}
                  </Badge>
                  <span className="text-sm font-semibold">
                    {disclosure.title}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("This value will be applied to")}{" "}
                  <strong>{orgCount}</strong> {t("organization(s)")}
                </p>
              </div>

              {loadingOrgTree || !orgTreeLoaded ? (
                <div className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
                  {t("Loading organizations...")}
                </div>
              ) : (
                <TargetValueFields
                  disclosure={disclosure}
                  values={values}
                  onChange={setValues}
                  disabled={bulkSetting}
                  inputType={inputType}
                  unitOptions={unitOptions}
                  isMonthly={isMonthly}
                  monthOptions={monthOptions}
                />
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={closeSetValueToAllDialog}
                  disabled={bulkSetting}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={
                    bulkSetting ||
                    loadingOrgTree ||
                    !canApply ||
                    !hasValue ||
                    hasInvalidNegativeZero
                  }
                >
                  {t("Apply to All")}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  );
});

export default SetValueToAllDialog;
